import { useEffect, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";

import Panel from "../../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../../components/PartnerDashboard/Skeleton";
import InfoTip from "../../../../components/PartnerDashboard/InfoTip";
import {
  Button,
  Empty,
  ErrorText,
  Note,
  PageStack,
  PrimaryText,
  SecondaryText,
  TableScroll,
  Td,
  Th,
} from "../../../../components/PartnerDashboard/ui";
import { adminGetReferralsReport } from "../../../../api/apiEndpoints";
import { downloadCsv } from "../../../../utils/reportDates";
import RangePicker, { useRange } from "../RangePicker";
import { ActionRow, Metric, MetricGrid, MetricLabel, MetricValue, StackTable } from "../ui";

const SHARE_TRACKING_START = "2026-10-13";
const PAGE_SIZE = 15;

interface Summary {
  shares: number;
  sharers: number;
  friends: number;
  viaLink: number;
  viaCode: number;
  paid: number;
}

interface Referrer {
  userId: string;
  name: string | null;
  email: string | null;
  shares: number;
  friends: number;
  viaLink: number;
  viaCode: number;
  paid: number;
}

interface Friend {
  name: string | null;
  email: string | null;
  referrerName: string | null;
  referrerEmail: string | null;
  firstAt: string;
  viaLink: boolean;
  viaCode: boolean;
  paid: boolean;
}

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b6259;
`;

const isoDate = (v: string) => new Date(v).toISOString().slice(0, 10);

const ReferralsPage = () => {
  const range = useRange("last30");
  const [data, setData] = useState<{ summary: Summary; referrers: Referrer[]; friends: Friend[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (range.invalid) return;
    let cancelled = false;
    setData(null);
    setError(null);
    setPage(1);
    adminGetReferralsReport(range.range.start, range.endExclusive)
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setError("Couldn't load referrals. Please try again."));
    return () => {
      cancelled = true;
    };
  }, [range.range.start, range.endExclusive, range.invalid]);

  const fileSuffix = `${range.range.start} to ${range.range.end}`;
  const s = data?.summary;
  const referrers = data?.referrers ?? [];
  const pageCount = Math.max(1, Math.ceil(referrers.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const rows = referrers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const downloadReferrers = () =>
    downloadCsv(
      [
        ["Referrer", "Email", "Shares", "Users referred", "Referred by link", "Referred by checkout code", "Referred users who paid"],
        ...referrers.map((r) => [r.name, r.email, r.shares, r.friends, r.viaLink, r.viaCode, r.paid]),
      ],
      `Referrers ${fileSuffix}.csv`,
    );

  const downloadFriends = () =>
    downloadCsv(
      [
        ["Referred user", "Email", "Referred by", "Referrer email", "Joined", "Joined by link", "Used code at checkout", "Paid for a membership"],
        ...(data?.friends ?? []).map((f) => [
          f.name,
          f.email,
          f.referrerName,
          f.referrerEmail,
          isoDate(f.firstAt),
          f.viaLink ? "Yes" : "No",
          f.viaCode ? "Yes" : "No",
          f.paid ? "Yes" : "No",
        ]),
      ],
      `Referred users ${fileSuffix}.csv`,
    );

  const tiles: { label: string; value: number | undefined; info?: string }[] = [
    { label: "Referrals shared", value: s?.shares, info: "Times a member tapped Share on their referral code. This counts the share sheet opening, not how many people received it." },
    { label: "Members who shared", value: s?.sharers },
    { label: "Users referred", value: s?.friends, info: "People who joined through a member's referral link or used their referral code at checkout. Each person counts once." },
    { label: "Referred by link", value: s?.viaLink, info: "Referred users who opened a referral link and signed in, whether or not the referrer earned a free class drop for it." },
    { label: "Referred by checkout code", value: s?.viaCode },
    { label: "Referred users who paid", value: s?.paid, info: "Referred users who bought a membership. Everyone who used a code at checkout paid." },
  ];

  return (
    <PageStack>
      <Panel
        title="Period"
        subtitle={range.label ? `${range.label} · Eastern time` : undefined}
        action={
          <ActionRow>
            <Button disabled={!data || referrers.length === 0} onClick={downloadReferrers}>
              <Download size={15} />
              Referrers CSV
            </Button>
            <Button disabled={!data || (data.friends ?? []).length === 0} onClick={downloadFriends}>
              <Download size={15} />
              Referred users CSV
            </Button>
          </ActionRow>
        }
      >
        <RangePicker state={range} />
        {error && <ErrorText>{error}</ErrorText>}
        {!range.invalid && range.range.start < SHARE_TRACKING_START && (
          <Note>Shares are only tracked from Oct 13, 2026, so shares before then aren't counted.</Note>
        )}
      </Panel>

      {!error && (
        <>
          <MetricGrid>
            {tiles.map((t) => (
              <Metric key={t.label}>
                {s ? <MetricValue>{(t.value ?? 0).toLocaleString()}</MetricValue> : <Skeleton $w="40%" $h="24px" $radius="8px" />}
                <MetricLabel>
                  {t.label}
                  {t.info && <InfoTip text={t.info} label={`About ${t.label}`} />}
                </MetricLabel>
              </Metric>
            ))}
          </MetricGrid>

          <Panel title="Top referrers" subtitle="Members who shared or referred someone this period">
            {!data ? (
              <Skeleton $h="160px" $radius="12px" />
            ) : referrers.length === 0 ? (
              <Empty>No referral activity in this period.</Empty>
            ) : (
              <TableScroll>
                <StackTable>
                  <thead>
                    <tr>
                      <Th>Referrer</Th>
                      <Th $align="right">Shares</Th>
                      <Th $align="right">Users referred</Th>
                      <Th $align="right">Referred users who paid</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.userId}>
                        <Td>
                          <PrimaryText>{r.name ?? "Unknown member"}</PrimaryText>
                          <SecondaryText>{r.email ?? ""}</SecondaryText>
                        </Td>
                        <Td data-label="Shares" $align="right">{r.shares}</Td>
                        <Td data-label="Users referred" $align="right">
                          <div>
                            {r.friends}
                            {r.friends > 0 && (
                              <SecondaryText>
                                {r.viaLink} by link · {r.viaCode} by code
                              </SecondaryText>
                            )}
                          </div>
                        </Td>
                        <Td data-label="Referred users who paid" $align="right">
                          <PrimaryText>{r.paid}</PrimaryText>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </StackTable>
              </TableScroll>
            )}
            {pageCount > 1 && (
              <Pager>
                <Button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
                  ← Prev
                </Button>
                <span>
                  Page {currentPage} of {pageCount} · {referrers.length} referrers
                </span>
                <Button disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>
                  Next →
                </Button>
              </Pager>
            )}
          </Panel>
        </>
      )}
    </PageStack>
  );
};

export default ReferralsPage;
